import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft } from "lucide-react";
import { buildHead, pickLang, localized, type SeoLang } from "@/lib/seo";
import { useI18n } from "@/lib/i18n";

const A_COPY: Record<SeoLang, { back: string; locale: string }> = {
  es: { back: "Volver al blog", locale: "es-PY" },
  en: { back: "Back to the blog", locale: "en-US" },
  pt: { back: "Voltar ao blog", locale: "pt-BR" },
};

const articleQuery = (slug: string) =>
  queryOptions({
    queryKey: ["blog", "detail", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("slug", slug)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw notFound();
      return data;
    },
  });

export const Route = createFileRoute("/_public/blog/$slug")({
  loader: ({ params, context }) => context.queryClient.ensureQueryData(articleQuery(params.slug)),
  head: ({ params, loaderData, match }) => {
    const lang = pickLang(match.search);
    if (!loaderData) {
      return {
        meta: [
          { title: "Artículo no disponible — Blog Dahiana Marín" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const a = loaderData as Record<string, unknown>;
    const baseTitle = localized(a, "title", lang) ?? "";
    const title = localized(a, "seo_title", lang) || `${baseTitle} — Dahiana Marín`;
    const description =
      localized(a, "seo_description", lang) || localized(a, "excerpt", lang) || baseTitle;
    const image = (a["cover_url"] as string | null) || undefined;
    const { meta, links, url } = buildHead({
      lang,
      path: `/blog/${params.slug}`,
      title,
      description,
      ogType: "article",
      image,
    });
    const absImage = image
      ? image.startsWith("http")
        ? image
        : `https://dahianamarin.com${image.startsWith("/") ? "" : "/"}${image}`
      : undefined;
    const article: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: baseTitle.slice(0, 110),
      description,
      inLanguage: lang === "es" ? "es-PY" : lang === "en" ? "en-US" : "pt-BR",
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: {
        "@type": "Person",
        name: "Dahiana Marín",
        url: "https://dahianamarin.com/agente",
      },
      publisher: { "@id": "https://dahianamarin.com/#agencia" },
    };
    if (absImage) article["image"] = [absImage];
    if (a["published_at"]) article["datePublished"] = a["published_at"];
    if (a["updated_at"] ?? a["published_at"]) article["dateModified"] = a["updated_at"] ?? a["published_at"];

    return {
      meta,
      links,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(article),
        },
      ],
    };
  },

  component: ArticlePage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-2xl font-semibold">Artículo no disponible</h1>
      <Link to="/blog" className="mt-4 inline-block text-primary underline">
        Volver al blog
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-3xl px-4 py-20 text-center">
      <h1 className="text-xl font-semibold">No pudimos cargar el artículo</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
});

function ArticlePage() {
  const { slug } = Route.useParams();
  const { lang } = useI18n();
  const copy = A_COPY[lang];
  const { data } = useSuspenseQuery(articleQuery(slug));
  const row = data as Record<string, unknown>;
  const title = localized(row, "title", lang) ?? "";
  const excerpt = localized(row, "excerpt", lang);
  const body = localized(row, "body_html", lang);

  return (
    <article className="mx-auto max-w-3xl px-4 py-14 md:px-6">
      <Link
        to="/blog"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> {copy.back}
      </Link>

      <header className="mt-6">
        {data.published_at && (
          <time className="text-xs uppercase tracking-wider text-accent">
            {new Date(data.published_at).toLocaleDateString(copy.locale, {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </time>
        )}
        <h1 className="mt-2 font-serif text-4xl text-foreground md:text-5xl">{title}</h1>
        {excerpt && <p className="mt-4 text-lg text-muted-foreground">{excerpt}</p>}
      </header>

      {data.cover_url && (
        <div className="mt-8 overflow-hidden rounded-xl">
          <img src={data.cover_url} alt={title} width={1200} height={670} className="aspect-[16/9] w-full object-cover" />
        </div>
      )}

      {body && (
        <div
          className="prose prose-neutral mt-10 max-w-none prose-headings:font-serif prose-a:text-primary"
          dangerouslySetInnerHTML={{ __html: body }}
        />
      )}
    </article>
  );
}
